import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, Image, ShoppingBag, Camera, Palette, Clock, Star } from 'lucide-react';

const AIImageGeneratorEtsy = () => {
  const faqs = [
    {
      question: "What image sizes work best for Etsy listings?",
      answer: "Etsy recommends 2000x2000px square images for listings. Our AI generator creates high-resolution images that meet Etsy's requirements, with options for lifestyle shots, white backgrounds, and mockups."
    },
    {
      question: "Can I create product mockups for my Etsy shop?",
      answer: "Absolutely! Our AI excels at generating realistic product mockups, lifestyle photography, and styled flat lays. Perfect for digital products, printables, jewelry, clothing, and handmade items."
    },
    {
      question: "Are AI-generated images allowed on Etsy?",
      answer: "Yes, AI-generated images are allowed for product photography and marketing. Our platform provides full commercial rights for all generated images, making them safe to use in your Etsy listings."
    },
    {
      question: "How many Etsy listing images can I create at once?",
      answer: "With our bulk generation feature, you can create hundreds of unique product images in minutes. Generate variations for A/B testing, seasonal themes, or complete product catalogs efficiently."
    }
  ];

  const features = [
    {
      icon: <Image className="w-6 h-6" />,
      title: "Etsy-Optimized Sizes",
      description: "Auto-generate 2000x2000px square images that meet Etsy's listing requirements perfectly"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Lifestyle Photography",
      description: "Create stunning lifestyle shots and styled flat lays that showcase your products beautifully"
    },
    {
      icon: <ShoppingBag className="w-6 h-6" />,
      title: "Product Mockups",
      description: "Generate realistic mockups for t-shirts, mugs, totes, jewelry, and digital products"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Seasonal Themes",
      description: "Create holiday-themed images for Christmas, Halloween, Valentine's, and seasonal promotions"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Bulk Generation",
      description: "Generate 100+ unique listing images in minutes instead of hours of photography"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Save 20+ Hours/Week",
      description: "Automate your product photography workflow and focus on creating and selling"
    }
  ];

  const useCases = [
    "Handmade jewelry sellers needing consistent product shots",
    "Digital download creators showcasing printables",
    "Vintage resellers creating styled product photos",
    "T-shirt and apparel sellers generating mockups",
    "Home decor artisans showing items in styled settings",
    "Craft supply sellers creating appealing catalogs",
    "Wedding vendors generating themed promotional images",
    "Sticker and planner shops creating lifestyle previews"
  ];

  return (
    <>
      <SEO
        title="AI Image Generator for Etsy Sellers | Product Photos That Sell"
        description="Create Etsy-optimized product photos with AI. Lifestyle shots, white backgrounds, mockups in seconds. Boost your Etsy shop sales with professional images. Used by 10,000+ sellers."
        keywords="ai image generator etsy, etsy product photos ai, etsy listing images, etsy shop photography, etsy mockup generator, etsy seller tools, best images for etsy, etsy product photography ai, etsy digital product mockups"
        url="/ai-image-generator-for-etsy"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI Image Generator for Etsy', url: '/ai-image-generator-for-etsy' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-amber-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-orange-500/10 text-orange-500 border-orange-500/20">
                Trusted by 10,000+ Etsy Sellers
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI Image Generator for Etsy
                <span className="block text-orange-500">Product Photos That Sell</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Create stunning Etsy listing images with AI. Professional mockups, lifestyle photography, 
                and white background shots in seconds. No photography skills needed.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600">
                  <Link to="/try-generator">
                    Create Etsy Images Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Etsy Examples</Link>
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span>4.9/5 from 2,800+ reviews</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Full Commercial Rights</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-500" />
                  <span>Images in Seconds</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why Etsy Sellers Choose Our AI Generator
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border-orange-500/10 hover:border-orange-500/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-500 mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Perfect For Every Etsy Shop Type
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {useCases.map((useCase, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                    <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span>{useCase}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Creating Stunning Etsy Listings Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join 10,000+ Etsy sellers using AI to create professional product photos. 
                No photography skills or expensive equipment needed.
              </p>
              <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600">
                <Link to="/try-generator">
                  Generate Etsy Images Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIImageGeneratorEtsy;
